/**
 * SearchIndex Domain Model
 * 
 * Generic model for creating searchable indexes for any Firestore collection.
 * Not property-specific - can be used for properties, promoters, deals, etc.
 * 
 * Architecture:
 * - Domain Model handles business logic (token generation, normalization)
 * - Repository handles database operations
 * - Hook wraps repository for React components
 * 
 * Usage:
 * const index = new SearchIndex({
 *   sourceCollection: 'discovery_properties',
 *   indexCollection: 'discovery_properties_index',
 *   searchableFields: ['address', 'postcode', 'town'],
 *   filterableFields: ['propertyStatus', 'deliveryStatus', 'price']
 * });
 */

// A UK postcode, whole or partial, with the space removed: "br76", "br76eh",
// "sw1a1aa". Anchored, so "northend" and "b2apartments" do not qualify.
const POSTCODE_LIKE = /^[a-z]{1,2}\d[a-z\d]?\d?[a-z]{0,2}$/;

export class SearchIndex {
  /**
   * Create a SearchIndex configuration
   * @param {Object} config
   * @param {string} config.sourceCollection - Source collection name
   * @param {string} config.indexCollection - Index collection name
   * @param {string[]} config.searchableFields - Fields to tokenize for search
   * @param {string[]} config.filterableFields - Fields to copy for filtering
   * @param {Object} config.functionNames - Optional explicit Cloud Function names
   * @param {string} config.functionNames.rebuild - Rebuild function name
   * @param {string} config.functionNames.getStats - Get stats function name
   * @param {Function} config.customTokenizer - Optional custom tokenizer function
   */
  constructor(config) {
    this.sourceCollection = config.sourceCollection;
    this.indexCollection = config.indexCollection;
    this.searchableFields = config.searchableFields || [];
    this.filterableFields = config.filterableFields || [];
    this.functionNames = config.functionNames; // Store explicit function names
    this.customTokenizer = config.customTokenizer;
    
    // Validate required fields
    if (!this.sourceCollection || !this.indexCollection) {
      throw new Error('SearchIndex requires sourceCollection and indexCollection');
    }
  }

  /**
   * Generate search tokens from a document
   * Uses n-gram tokenization for partial matching
   * 
   * @param {Object} doc - Source document
   * @returns {string[]} Array of search tokens
   */
  generateTokens(doc) {
    if (this.customTokenizer) {
      return this.customTokenizer(doc);
    }

    const tokens = new Set();

    // Process each searchable field
    this.searchableFields.forEach(field => {
      const value = doc[field];
      if (!value) return;

      const text = String(value).toLowerCase();
      
      // Split into words and create tokens
      const words = text.split(/[\s,.-]+/).filter(w => w.length > 0);
      
      words.forEach(word => {
        // Add full word
        tokens.add(word);
        
        // Add n-grams (substrings) for partial matching
        // Start from length 3 to avoid too many tokens
        if (word.length >= 3) {
          for (let i = 3; i <= word.length; i++) {
            tokens.add(word.substring(0, i));
          }
        }
      });

      // For postcodes, add normalized version (no spaces)
      if (field.toLowerCase().includes('postcode')) {
        const normalized = text.replace(/\s+/g, '');
        tokens.add(normalized); // Full postcode
        
        // Add postcode prefixes for UK postcodes (2+ chars)
        // Examples: CF, CF8, CF83, B2, B23, SW1, SW15
        if (normalized.length >= 2) {
          for (let i = 2; i <= Math.min(normalized.length, 7); i++) {
            tokens.add(normalized.substring(0, i));
          }
        }
      }
    });

    return Array.from(tokens).filter(t => t.length >= 2); // Min 2 chars
  }

  /**
   * Create normalized searchable strings for exact/contains matching
   * @param {Object} doc - Source document
   * @returns {Object} Normalized field values
   */
  generateNormalizedFields(doc) {
    const normalized = {};

    this.searchableFields.forEach(field => {
      const value = doc[field];
      if (value) {
        const key = `normalized_${field}`;
        normalized[key] = String(value)
          .toLowerCase()
          .replace(/\s+/g, '')
          .replace(/[^a-z0-9]/g, '');
      }
    });

    return normalized;
  }

  /**
   * Create a search index document from source document
   * @param {string} docId - Document ID
   * @param {Object} doc - Source document data
   * @returns {Object} Search index document
   */
  toSearchDoc(docId, doc) {
    const searchDoc = {
      sourceId: docId,
      sourceCollection: this.sourceCollection,
      searchTokens: this.generateTokens(doc),
      ...this.generateNormalizedFields(doc),
      updatedAt: new Date()
    };

    // Copy filterable fields
    this.filterableFields.forEach(field => {
      if (doc[field] !== undefined) {
        searchDoc[field] = doc[field];
      }
    });

    return searchDoc;
  }

  /**
   * Turn what someone typed into a query plan.
   *
   * ⚠️ This replaced parseSearchTerm, which made ANY multi-word search that was
   * not a postcode return nothing (measured 10 Sep 2026 on 200 random live
   * properties: street name 13/200 found, "12 Broadheath Drive" 0/200, the
   * address as displayed 0/200). It glued the words together ("12broadheathdrive")
   * and made that the term the query ran on — a token the index never holds
   * outside postcodes — and it split on spaces only, so "12," never matched "12".
   *
   * The plan mirrors how the SERVER tokenises (functions/searchIndex.js
   * generateSearchTokens — the live index is built there, not by generateTokens
   * above, whose postcode prefixes have drifted):
   *   - words split on space, comma, full stop and hyphen, as the index is;
   *   - words of 3+ characters are index tokens whenever they begin an indexed
   *     word, so they are queried;
   *   - 1–2 character words ("12", "St", "Dr") are only tokens when a whole
   *     word matches exactly, so they are checked against the fetched
   *     property's own text instead of being required of the index;
   *   - the words run together are a candidate postcode token ("BR7 6" →
   *     "br76") — but ONLY when the result looks like a postcode. Run together
   *     unconditionally, "North End" became "northend", a real word in some
   *     other address, and was then required of every result, excluding the
   *     North End that was asked for. The repository also uses it only if the
   *     index actually has it.
   *
   * @param {string} searchTerm
   * @returns {{indexTerms: string[], shortWords: string[], concat: string|null}}
   */
  planSearch(searchTerm) {
    const words = String(searchTerm || '')
      .toLowerCase()
      .split(/[\s,.-]+/)
      .filter(Boolean);
    const unique = [...new Set(words)];
    let indexTerms = unique.filter((w) => w.length >= 3);
    let shortWords = unique.filter((w) => w.length <= 2);
    // Nothing long enough: fall back to whole-word matches on 2-character words.
    if (!indexTerms.length) {
      indexTerms = shortWords.filter((w) => w.length === 2);
      shortWords = shortWords.filter((w) => w.length === 1);
    }
    const joined = words.join('');
    const concat =
      words.length > 1 && joined.length >= 3 && POSTCODE_LIKE.test(joined) ? joined : null;
    return { indexTerms, shortWords, concat };
  }

  /**
   * The term to run the query on: the one matching the FEWEST documents, so
   * the fetch is as small as possible and the result cap is least likely to
   * cut real matches. Ties go to the longer term.
   * @param {string[]} terms
   * @param {Object<string, number>} counts
   */
  static choosePrimary(terms, counts) {
    return terms.reduce((best, t) => {
      const a = counts[t] ?? Infinity;
      const b = counts[best] ?? Infinity;
      return a < b || (a === b && t.length > best.length) ? t : best;
    }, terms[0]);
  }

  /**
   * The index-side half of the short-word check, run BEFORE the result cap so
   * a common query term does not crowd real matches out ("THE PA": 1,390
   * entries contain "the"). Only 2-character words can be judged here: every
   * word of 2+ characters is stored whole, and longer words also as every
   * prefix of 3+, so "some token starts with 'pa'" is exactly "some word does".
   * 1-character words are not stored at all (house number "6"), so they are
   * left to matchesShortWords on the document's own text.
   */
  static tokensMatchShortWords(tokens, shortWords) {
    const twoChar = shortWords.filter((w) => w.length === 2);
    return twoChar.every((w) => tokens.some((t) => t.startsWith(w)));
  }

  /**
   * Every short word must begin a word in the document's searchable text —
   * the same prefix rule the index applies to longer words.
   */
  static matchesShortWords(doc, shortWords, fields) {
    if (!shortWords.length) return true;
    const words = fields
      .map((f) => String(doc?.[f] ?? '').toLowerCase())
      .join(' ')
      .split(/[\s,.-]+/)
      .filter(Boolean);
    return shortWords.every((w) => words.some((x) => x.startsWith(w)));
  }

  /**
   * Validate search index document
   * @param {Object} searchDoc - Search index document
   * @returns {Object} Validation result {valid: boolean, errors: string[]}
   */
  validate(searchDoc) {
    const errors = [];

    if (!searchDoc.sourceId) {
      errors.push('Missing sourceId');
    }

    if (!searchDoc.searchTokens || !Array.isArray(searchDoc.searchTokens)) {
      errors.push('Missing or invalid searchTokens array');
    }

    if (searchDoc.searchTokens && searchDoc.searchTokens.length === 0) {
      errors.push('Empty searchTokens array');
    }

    if (searchDoc.searchTokens && searchDoc.searchTokens.length > 20000) {
      errors.push('searchTokens array exceeds Firestore limit (20,000)');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate index statistics
   * @param {Object[]} indexDocs - Array of search index documents
   * @returns {Object} Statistics
   */
  static calculateStats(indexDocs) {
    if (!indexDocs || indexDocs.length === 0) {
      return {
        totalDocuments: 0,
        averageTokens: 0,
        maxTokens: 0,
        minTokens: 0,
        totalTokens: 0,
        lastUpdated: null
      };
    }

    const tokenCounts = indexDocs.map(doc => doc.searchTokens?.length || 0);
    const totalTokens = tokenCounts.reduce((sum, count) => sum + count, 0);
    const lastUpdated = indexDocs.reduce((latest, doc) => {
      const docDate = doc.updatedAt?.toDate?.() || doc.updatedAt;
      return !latest || docDate > latest ? docDate : latest;
    }, null);

    return {
      totalDocuments: indexDocs.length,
      averageTokens: Math.round(totalTokens / indexDocs.length),
      maxTokens: Math.max(...tokenCounts),
      minTokens: Math.min(...tokenCounts),
      totalTokens,
      lastUpdated
    };
  }
}

/**
 * Pre-configured SearchIndex for properties
 */
export const PropertySearchIndex = new SearchIndex({
  sourceCollection: 'discovery_properties',
  indexCollection: 'discovery_properties_index',
  searchableFields: ['address', 'postcode', 'town'],
  filterableFields: [
    'propertyStatus',
    'deliveryStatus', 
    'price',
    'dateAdded',
    'scrapeId',
    'isTestProperty'
  ],
  // Explicit Cloud Function names (as deployed)
  functionNames: {
    rebuild: 'rebuildPropertySearchIndex',
    getStats: 'getPropertySearchIndexStats'
  }
});

export default SearchIndex;
